import {createPortal} from 'react-dom'
import {useState, useRef, useEffect} from 'react'
import {
  AlignLeft,
  CalendarDays,
  Flag,
  Gauge,
  Layers3,
  LayoutPanelTop,
  Loader2,
  Paperclip,
  Search,
  Tag,
  UploadCloud,
  UserRound,
  X,
} from 'lucide-react'
import type {TaskStatus, Priority, Attachment} from '../../types'
import {useApp} from '../../context/AppContext'
import {uploadFile} from '../../services/api'
import RichTextEditor from '../common/RichTextEditor'
import Avatar from '../common/Avatar'
import ConfirmDialog from '../common/ConfirmDialog'
import useDialogAccessibility from '../common/useDialogAccessibility'
import {getStatusConfig} from '../../utils/helpers'

interface NewTaskFormProps {
  boardId: string
  initialStatus: TaskStatus
  onClose: () => void
}

interface PendingAttachment {
  file: File
  name: string
}

export default function NewTaskForm({boardId, initialStatus, onClose}: NewTaskFormProps) {
  const {state, apiCreateTask} = useApp()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<TaskStatus>(initialStatus)
  const [priority, setPriority] = useState<Priority>('medium')
  const [storyPoints, setStoryPoints] = useState(0)
  const [sprintId, setSprintId] = useState('')
  const [assigneeIds, setAssigneeIds] = useState<string[]>([])
  const [assigneeSearch, setAssigneeSearch] = useState('')
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false)
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([])
  const [dueDate, setDueDate] = useState('')
  const [pendingFiles, setPendingFiles] = useState<PendingAttachment[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [descriptionUploading, setDescriptionUploading] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)

  const uploadedFilesRef = useRef(new Map<File, number>())
  const assigneeContainerRef = useRef<HTMLDivElement>(null)

  const board = state.boards.find((item) => item.id === boardId)
  const availableStatuses = board?.columns.map((column) => column.status) || [initialStatus]
  const boardSprints = state.sprints.filter((sprint) => sprint.boardId === boardId)
  const selectedAssignees = state.users.filter((user) => assigneeIds.includes(user.id))
  const statusConfig = getStatusConfig(status)
  const hasDraftContent =
    !!title.trim() ||
    !!description.trim() ||
    assigneeIds.length > 0 ||
    selectedLabelIds.length > 0 ||
    pendingFiles.length > 0 ||
    !!dueDate ||
    !!sprintId ||
    storyPoints > 0 ||
    priority !== 'medium' ||
    status !== initialStatus

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        assigneeContainerRef.current &&
        !assigneeContainerRef.current.contains(e.target as Node)
      ) {
        setShowAssigneeDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filteredUsers = state.users.filter(
    (user) =>
      !assigneeIds.includes(user.id) &&
      user.name.toLowerCase().includes(assigneeSearch.toLowerCase())
  )

  const handleAddAssignee = (userId: string) => {
    setAssigneeIds((prev) => [...prev, userId])
    setAssigneeSearch('')
    setShowAssigneeDropdown(false)
  }

  const handleRemoveAssignee = (userId: string) => {
    setAssigneeIds((prev) => prev.filter((id) => id !== userId))
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return
    const newFiles = Array.from(files).map((file) => ({
      file,
      name: file.name,
    }))
    setPendingFiles((prev) => [
      ...prev,
      ...newFiles.filter(
        (next) =>
          !prev.some(
            (item) =>
              item.file.name === next.file.name &&
              item.file.size === next.file.size &&
              item.file.lastModified === next.file.lastModified
          )
      ),
    ])
    e.target.value = ''
  }

  const handleRemoveFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const toggleLabel = (labelId: string) => {
    setSelectedLabelIds((prev) =>
      prev.includes(labelId) ? prev.filter((id) => id !== labelId) : [...prev, labelId]
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || submitting || descriptionUploading) return

    setSubmitting(true)
    setSubmitError('')
    try {
      const uploadedAttachments: Partial<Attachment>[] = []

      for (const pending of pendingFiles) {
        let uploadId = uploadedFilesRef.current.get(pending.file)
        if (uploadId === undefined) {
          const uploadResult = await uploadFile(pending.file)
          uploadId = uploadResult.id_upload
          uploadedFilesRef.current.set(pending.file, uploadId)
        }
        uploadedAttachments.push({
          name: pending.file.name,
          size: pending.file.size,
          type: pending.file.type,
          id_upload: uploadId,
        })
      }

      await apiCreateTask({
        title: title.trim(),
        description: description.trim(),
        status,
        priority,
        assigneeIds,
        reporterId: state.currentUser?.id_user || '',
        boardId,
        sprintId: sprintId || null,
        labelIds: selectedLabelIds,
        comments: [],
        attachments: uploadedAttachments as Attachment[],
        subtasks: [],
        storyPoints,
        dueDate: dueDate || null,
      })
      onClose()
    } catch (error) {
      console.error('Failed to create task:', error)
      setSubmitError('Não foi possível criar a tarefa. Revise os dados e tente novamente.')
    } finally {
      setSubmitting(false)
    }
  }

  const requestClose = () => {
    if (submitting || descriptionUploading) return
    if (hasDraftContent) {
      setShowDiscardConfirm(true)
      return
    }
    onClose()
  }

  const dialogRef = useDialogAccessibility<HTMLFormElement>(
    true,
    requestClose,
    submitting || descriptionUploading
  )

  const formatFileSize = (size: number) => {
    if (size < 1024) return `${size} B`
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
    return `${(size / (1024 * 1024)).toFixed(1)} MB`
  }

  return createPortal(
    <div className='devops-task-modal-root fixed inset-0 z-[1200] flex items-center justify-center p-2 md:p-4 animate-fade-in'>
      <div className='devops-task-modal-backdrop' onClick={requestClose} aria-hidden='true' />

      <form
        ref={dialogRef}
        tabIndex={-1}
        aria-busy={submitting || descriptionUploading}
        onSubmit={handleSubmit}
        className='devops-task-workspace devops-task-workspace--create animate-scale-in'
        role='dialog'
        aria-modal='true'
        aria-labelledby='new-task-modal-title'
      >
        <header className='devops-task-modal-header'>
          <div className='devops-task-modal-heading'>
            <span className='devops-task-modal-heading__icon'>
              <LayoutPanelTop size={20} strokeWidth={1.9} />
            </span>
            <div className='min-w-0'>
              <div className='devops-task-modal-kicker'>
                <span>{board?.name || 'Quadro de trabalho'}</span>
                <span>Nova tarefa</span>
                <span>{statusConfig.label}</span>
              </div>
              <h2 id='new-task-modal-title' className='devops-task-modal-title'>
                Criar nova tarefa
              </h2>
            </div>
          </div>

          <button
            type='button'
            onClick={requestClose}
            disabled={submitting || descriptionUploading}
            className='devops-icon-button'
            aria-label='Fechar modal'
          >
            <X size={19} />
          </button>
        </header>

        <fieldset
          disabled={submitting || descriptionUploading}
          className='devops-task-modal-body m-0 min-w-0 border-0 p-0'
        >
          <main className='devops-task-main'>
            <section className='devops-task-form-section'>
              <h3 className='devops-task-form-section__title'>
                <AlignLeft size={17} />
                Informações principais
              </h3>

              <div>
                <label
                  htmlFor='task-title'
                  className='mb-1.5 block text-sm font-semibold text-[#264073]'
                >
                  Título da tarefa
                </label>
                <input
                  id='task-title'
                  type='text'
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder='Ex.: Revisar integração do fluxo de pedidos'
                  className='w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100'
                  autoFocus
                  data-dialog-autofocus
                  maxLength={255}
                  required
                />
                <p className='mt-1.5 text-[11px] text-slate-400'>
                  Use um título curto, específico e orientado à ação.
                </p>
              </div>

              <div className='mt-5'>
                <label className='mb-1.5 block text-sm font-semibold text-[#264073]'>
                  Descrição
                </label>
                <RichTextEditor
                  onBusyChange={setDescriptionUploading}
                  disabled={submitting}
                  value={description}
                  onChange={setDescription}
                  placeholder='Adicione contexto, objetivo, critérios de aceite e links importantes...'
                  minHeight='210px'
                />
              </div>
            </section>

            <section className='devops-task-form-section'>
              <h3 className='devops-task-form-section__title'>
                <Paperclip size={17} />
                Anexos
                {pendingFiles.length > 0 && (
                  <span className='rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-bold text-brand-600'>
                    {pendingFiles.length}
                  </span>
                )}
              </h3>

              <label htmlFor='task-files' className='devops-task-upload-zone'>
                <div>
                  <UploadCloud size={26} className='mx-auto mb-2' />
                  <strong className='block text-sm'>Adicionar arquivos à tarefa</strong>
                  <span className='mt-1 block text-xs text-slate-400'>
                    Selecione um ou vários arquivos do computador
                  </span>
                </div>
                <input
                  id='task-files'
                  type='file'
                  multiple
                  onChange={handleFileChange}
                  className='hidden'
                />
              </label>

              {pendingFiles.length > 0 && (
                <div className='mt-3 grid grid-cols-1 gap-2 md:grid-cols-2'>
                  {pendingFiles.map((pending, index) => (
                    <div
                      key={`${pending.name}-${index}`}
                      className='flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-2.5'
                    >
                      <span className='grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white text-brand-500 shadow-sm'>
                        <Paperclip size={15} />
                      </span>
                      <span className='min-w-0 flex-1'>
                        <strong className='block truncate text-xs font-semibold text-slate-700'>
                          {pending.name}
                        </strong>
                        <small className='text-[10px] text-slate-400'>
                          {formatFileSize(pending.file.size)}
                        </small>
                      </span>
                      <button
                        type='button'
                        onClick={() => handleRemoveFile(index)}
                        className='rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500'
                        aria-label={`Remover ${pending.name}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </main>

          <aside className='devops-task-sidebar'>
            <section className='devops-task-sidebar-section'>
              <h3 className='devops-task-sidebar-title'>
                <Flag size={15} />
                Planejamento
              </h3>

              <div className='devops-task-form-grid'>
                <div className='devops-task-field'>
                  <label htmlFor='task-status' className='devops-task-field__label'>
                    Status
                  </label>
                  <select
                    id='task-status'
                    value={status}
                    onChange={(event) => setStatus(event.target.value)}
                    className='w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm'
                  >
                    {availableStatuses.map((item) => (
                      <option key={item} value={item}>
                        {getStatusConfig(item).label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className='devops-task-field'>
                  <label htmlFor='task-priority' className='devops-task-field__label'>
                    Prioridade
                  </label>
                  <select
                    id='task-priority'
                    value={priority}
                    onChange={(event) => setPriority(event.target.value as Priority)}
                    className='w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm'
                  >
                    <option value='low'>Baixa</option>
                    <option value='medium'>Média</option>
                    <option value='high'>Alta</option>
                    <option value='critical'>Crítica</option>
                  </select>
                </div>

                <div className='devops-task-field'>
                  <label htmlFor='task-story-points' className='devops-task-field__label'>
                    <Gauge size={13} />
                    Pontos
                  </label>
                  <input
                    id='task-story-points'
                    type='number'
                    min={0}
                    max={100}
                    value={storyPoints}
                    onChange={(event) =>
                      setStoryPoints(Math.max(0, Number(event.target.value) || 0))
                    }
                    className='w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm'
                  />
                </div>

                <div className='devops-task-field'>
                  <label htmlFor='task-due-date' className='devops-task-field__label'>
                    <CalendarDays size={13} />
                    Prazo
                  </label>
                  <input
                    id='task-due-date'
                    type='date'
                    value={dueDate}
                    onChange={(event) => setDueDate(event.target.value)}
                    className='w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm'
                  />
                </div>
              </div>

              <div className='devops-task-field mt-4'>
                <label htmlFor='task-sprint' className='devops-task-field__label'>
                  <Layers3 size={13} />
                  Sprint
                </label>
                <select
                  id='task-sprint'
                  value={sprintId}
                  onChange={(event) => setSprintId(event.target.value)}
                  className='w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm'
                >
                  <option value=''>Sem sprint</option>
                  {boardSprints.map((sprint) => (
                    <option key={sprint.id} value={sprint.id}>
                      {sprint.name}
                    </option>
                  ))}
                </select>
              </div>
            </section>

            <section className='devops-task-sidebar-section'>
              <h3 className='devops-task-sidebar-title'>
                <UserRound size={15} />
                Responsáveis
              </h3>

              {selectedAssignees.length > 0 && (
                <div className='mb-3 space-y-2'>
                  {selectedAssignees.map((user) => (
                    <div
                      key={user.id}
                      className='flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2'
                    >
                      <Avatar name={user.name} color={user.avatarColor} size='sm' />
                      <span className='min-w-0 flex-1 truncate text-sm text-slate-700'>
                        {user.name}
                      </span>
                      <button
                        type='button'
                        onClick={() => handleRemoveAssignee(user.id)}
                        className='rounded p-0.5 text-slate-300 hover:bg-red-50 hover:text-red-500'
                        aria-label={`Remover ${user.name}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div
                ref={assigneeContainerRef}
                className='relative'
                onKeyDown={(event) => {
                  if (event.key === 'Escape' && showAssigneeDropdown) {
                    event.preventDefault()
                    event.stopPropagation()
                    setShowAssigneeDropdown(false)
                  }
                }}
              >
                <div className='flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 focus-within:border-brand-400 focus-within:ring-2 focus-within:ring-brand-100'>
                  <Search size={15} className='shrink-0 text-slate-400' />
                  <input
                    type='text'
                    value={assigneeSearch}
                    onChange={(event) => {
                      setAssigneeSearch(event.target.value)
                      setShowAssigneeDropdown(true)
                    }}
                    onFocus={() => setShowAssigneeDropdown(true)}
                    placeholder='Buscar pessoa...'
                    className='w-full bg-transparent text-sm outline-none'
                    aria-label='Buscar responsável'
                  />
                </div>

                {showAssigneeDropdown && (
                  <div className='absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg'>
                    <div className='max-h-52 overflow-y-auto py-1'>
                      {filteredUsers.map((user) => (
                        <button
                          key={user.id}
                          type='button'
                          onClick={() => handleAddAssignee(user.id)}
                          className='flex w-full items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-brand-50'
                        >
                          <Avatar name={user.name} color={user.avatarColor} size='sm' />
                          <span className='truncate'>{user.name}</span>
                        </button>
                      ))}
                      {filteredUsers.length === 0 && (
                        <div className='px-3 py-3 text-center text-xs text-slate-400'>
                          Nenhuma pessoa encontrada
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </section>

            <section className='devops-task-sidebar-section'>
              <h3 className='devops-task-sidebar-title'>
                <Tag size={15} />
                Etiquetas
              </h3>
              {state.labels.length > 0 ? (
                <div className='flex flex-wrap gap-2'>
                  {state.labels.map((label) => {
                    const selected = selectedLabelIds.includes(label.id)
                    return (
                      <button
                        key={label.id}
                        type='button'
                        onClick={() => toggleLabel(label.id)}
                        className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-all ${
                          selected
                            ? 'border-brand-300 ring-2 ring-brand-100'
                            : 'border-slate-200 hover:border-brand-200'
                        }`}
                        style={{backgroundColor: `${label.color}16`, color: label.color}}
                        aria-pressed={selected}
                      >
                        {label.name}
                      </button>
                    )
                  })}
                </div>
              ) : (
                <p className='m-0 text-xs text-slate-400'>
                  Nenhuma etiqueta disponível neste workspace.
                </p>
              )}
            </section>
          </aside>
        </fieldset>

        <footer className='devops-task-modal-footer'>
          <p role={submitError ? 'alert' : 'status'} className={submitError ? '!text-red-600' : ''}>
            {submitError ||
              'Preencha o título; os demais campos podem ser ajustados agora ou depois.'}
          </p>
          <div className='devops-task-modal-footer__actions'>
            <button
              type='button'
              onClick={requestClose}
              className='devops-secondary-button'
              disabled={submitting || descriptionUploading}
            >
              Cancelar
            </button>
            <button
              type='submit'
              disabled={!title.trim() || submitting || descriptionUploading}
              className='devops-primary-button min-w-[150px] disabled:cursor-not-allowed disabled:opacity-50'
            >
              {submitting && <Loader2 size={15} className='animate-spin' />}
              {submitting ? 'Criando tarefa...' : 'Criar tarefa'}
            </button>
          </div>
        </footer>
      </form>

      <ConfirmDialog
        isOpen={showDiscardConfirm}
        title='Descartar nova tarefa'
        message='Os dados preenchidos ainda não foram salvos. Deseja fechar e descartá-los?'
        confirmLabel='Descartar'
        onConfirm={() => {
          setShowDiscardConfirm(false)
          onClose()
        }}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </div>,
    document.body
  )
}
